import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import type {
  HorizonConfig,
  HorizonEventLog,
  HorizonIngestApp,
  HorizonOptOutDateRange,
  HorizonOptOutDetails,
  HorizonRecordFlag,
  HorizonRegionFilter,
  HorizonTimeZone,
  HorizonTriggeredByFilter,
} from '../types/horizonOptOutTracker';
import { ALL_HORIZON_REGIONS, ALL_TRIGGERED_BY } from '../types/horizonOptOutTracker';

const eventTypes = {
  existingLeadExpired: 'existingleadfound_expired',
  phaseRulesEvaluated: 'phaserulesevaluated',
  worthEvaluationFailure: 'worthaievaluationfailure',
  worthEvaluated: 'worthaicheckevaluated',
} as const;

const eventOutcomes = {
  failed: 'failed',
} as const;

interface HorizonOptOutCsvRow {
  appUid?: string;
  companyName: string;
  email: string;
  optOutReason: string;
  optOutTime: string;
  recordFlag?: string;
  region: string;
  type: string;
}

const createCsvColumns = (timeZone: HorizonTimeZone): { header: string; key: keyof HorizonOptOutCsvRow }[] => [
  { header: 'App ID', key: 'appUid' },
  { header: 'Email', key: 'email' },
  { header: 'Company name', key: 'companyName' },
  { header: 'Region', key: 'region' },
  { header: 'Record flag', key: 'recordFlag' },
  { header: 'Opt out reason', key: 'optOutReason' },
  { header: 'Type', key: 'type' },
  { header: `Time of opt out (${timeZone})`, key: 'optOutTime' },
];

/**
 * Formats an opt-out timestamp in UTC or in the reader's own time zone.
 *
 * EventLogEntry returns `eventDateUTC` without a zone designator, which JavaScript reads as local
 * time, so the instant has to be pinned to UTC before it can be converted to anything else.
 */
export const formatHorizonOptOutTime = (optOutTimeUTC: string, timeZone: HorizonTimeZone): string => {
  const instant = /(Z|[+-]\d{2}:?\d{2})$/.test(optOutTimeUTC) ? optOutTimeUTC : `${optOutTimeUTC}Z`;
  return timeZone === 'UTC'
    ? formatInTimeZone(instant, 'UTC', 'dd MMM yyyy, HH:mm')
    : format(new Date(instant), 'dd MMM yyyy, HH:mm');
};

const escapeCsvValue = (value: string): string => {
  const safeValue = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safeValue) ? `"${safeValue.replaceAll('"', '""')}"` : safeValue;
};

/** Describes which source flags made an application part of the tracker results. */
export const getHorizonRecordFlag = ({
  isDisqualified,
  isOptedOut,
}: Pick<HorizonIngestApp, 'isDisqualified' | 'isOptedOut'>): HorizonRecordFlag => {
  if (isDisqualified && isOptedOut) return 'Disqualified and opted out';
  if (isDisqualified) return 'Disqualified';
  if (isOptedOut) return 'Opted out';
  return 'Unknown';
};

/** Masks a customer email address for display and export. */export const maskEmail = (email: string): string => {
  if (email === 'N/A') return email;

  const [localPart, domain, ...extraParts] = email.split('@');
  if (!localPart || !domain || extraParts.length > 0) return email;
  return `${localPart[0]}****@${domain}`;
};

/** Serializes the visible Horizon opt-out grid values as a UTF-8 CSV document. */
export const createHorizonOptOutCsv = (rows: HorizonOptOutCsvRow[], timeZone: HorizonTimeZone = 'UTC'): string => {
  const csvColumns = createCsvColumns(timeZone);
  return [
    csvColumns.map(({ header }) => escapeCsvValue(header)).join(','),
    ...rows.map((row) =>
      csvColumns
        .map(({ key }) => escapeCsvValue(key === 'email' ? maskEmail(row[key] ?? '') : (row[key] ?? '')))
        .join(','),
    ),
  ].join('\r\n');
};

const horizonConfigDefinitions = [
  { name: 'Horizon_US', region: 'US' },
  { name: 'Horizon_UK', region: 'UK' },
] as const;

const readValue = (value: unknown, keys: string[]): unknown => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;

  const record = value as Record<string, unknown>;
  const recordKeys = Object.keys(record);
  const key = keys
    .map((expected) => recordKeys.find((candidate) => candidate.toLowerCase() === expected.toLowerCase()))
    .find((candidate) => candidate !== undefined);
  return key ? record[key] : undefined;
};

const readString = (value: unknown, keys: string[]): string | undefined => {
  const candidate = readValue(value, keys);
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : undefined;
};

const readInteger = (value: unknown, keys: string[]): number | undefined => {
  const candidate = readValue(value, keys);
  return typeof candidate === 'number' && Number.isSafeInteger(candidate) && candidate >= 0 ? candidate : undefined;
};

const readBoolean = (value: unknown, keys: string[]): boolean | undefined => {
  const candidate = readValue(value, keys);
  if (typeof candidate === 'boolean') return candidate;
  if (typeof candidate !== 'string') return undefined;

  const normalizedValue = candidate.trim().toLowerCase();
  if (normalizedValue === 'true') return true;
  if (normalizedValue === 'false') return false;
  return undefined;
};

const isUuid = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const readCompanyName = (companyNameJson: unknown): string => {
  if (typeof companyNameJson !== 'string' || !companyNameJson.trim()) return 'N/A';

  try {
    const parsedValue: unknown = JSON.parse(companyNameJson);
    if (typeof parsedValue === 'string' && parsedValue.trim()) return parsedValue.trim();

    const namedValue = readString(parsedValue, [
      'companyDbaName',
      'companyLegalName',
      'companyName',
      'legalName',
      'dbaName',
      'doingBusinessAs',
      'name',
    ]);
    return namedValue ?? 'N/A';
  } catch {
    const trimmedValue = companyNameJson.trim();
    return trimmedValue.startsWith('{') || trimmedValue.startsWith('[') ? 'N/A' : trimmedValue;
  }
};

/**
 * Resolves the supported Horizon configurations present in the config-list response.
 *
 * Each environment seeds its own subset of Horizon configurations, so an absent configuration is
 * expected and simply omitted. A duplicated name or an invalid ID still indicates a broken response.
 */
export const resolveHorizonConfigs = (response: unknown): HorizonConfig[] => {
  if (!Array.isArray(response)) throw new Error('Kirby returned an invalid configuration list.');

  return horizonConfigDefinitions.flatMap(({ name, region }) => {
    const matches = response.filter((config) => readString(config, ['configName']) === name);
    if (matches.length === 0) return [];
    if (matches.length > 1) throw new Error(`Expected exactly one ${name} configuration.`);

    const id = readInteger(matches[0], ['id']);
    if (id === undefined) throw new Error(`${name} has an invalid configuration ID.`);
    return [{ id, name, region }];
  });
};

/** Normalizes the IngestApp fields required by the dashboard. */
export const normalizeHorizonIngestApp = (
  record: Record<string, unknown>,
  config: HorizonConfig,
): HorizonIngestApp | null => {
  const appUid = readString(record, ['appUid']);
  if (!appUid || !isUuid(appUid)) return null;

  const recordConfigId = readInteger(record, ['onDemandConfigId']);
  if (recordConfigId !== undefined && recordConfigId !== config.id) return null;

  return {
    appUid,
    companyName: readCompanyName(readValue(record, ['companyNameJson'])),
    email: readString(record, ['email']) ?? 'N/A',
    isDisqualified: readBoolean(record, ['isDisqualified']) ?? false,
    isOptedOut: readBoolean(record, ['isOptedOut']) ?? false,
    region: config.region,
  };
};

/** Normalizes the EventLog fields required by the dashboard. */
export const normalizeHorizonEventLog = (record: Record<string, unknown>): HorizonEventLog | null => {
  const appUid = readString(record, ['appUid']);
  const eventDateUTC = readString(record, ['eventDateUTC']);
  const eventType = readString(record, ['eventType']);
  const eventDescription = readString(record, ['eventDescription']);
  if (
    !appUid ||
    !isUuid(appUid) ||
    !eventDateUTC ||
    Number.isNaN(Date.parse(eventDateUTC)) ||
    !eventType ||
    !eventDescription
  ) {
    return null;
  }

  return { appUid, eventDateUTC, eventType, eventDescription };
};
/** Returns the default ten-day inclusive calendar range in UTC. */
export const getDefaultHorizonOptOutDateRange = (now = new Date()): HorizonOptOutDateRange => ({
  endDate: now.toISOString().slice(0, 10),
  startDate: new Date(now.getTime() - 9 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
});

/**
 * Summarises the applied filters for the collapsed filter header, so the reader can still see what
 * produced the results without expanding the card.
 */
export const describeHorizonOptOutFilters = (filters: {
  applicationSearch: string;
  dateRange: HorizonOptOutDateRange;
  optOutReason: string;
  region: HorizonRegionFilter;
  triggeredBy: HorizonTriggeredByFilter;
}): string => {
  const parts = [
    `${filters.dateRange.startDate} to ${filters.dateRange.endDate}`,
    filters.region === ALL_HORIZON_REGIONS ? 'both regions' : filters.region,
  ];
  if (filters.applicationSearch.trim()) parts.push(`search "${filters.applicationSearch.trim()}"`);
  if (filters.optOutReason.trim()) parts.push(`reason "${filters.optOutReason.trim()}"`);
  if (filters.triggeredBy !== ALL_TRIGGERED_BY) parts.push(`triggered by ${filters.triggeredBy}`);
  return parts.join(', ');
};

/** Converts selected calendar dates to inclusive UTC timestamps for Kirby filtering. */
export const getHorizonOptOutDateBounds = (
  dateRange: HorizonOptOutDateRange,
): { windowEnd: string; windowStart: string } => {
  const windowStart = new Date(`${dateRange.startDate}T00:00:00.000Z`);
  const windowEnd = new Date(`${dateRange.endDate}T23:59:59.999Z`);
  if (Number.isNaN(windowStart.getTime()) || Number.isNaN(windowEnd.getTime()) || windowStart > windowEnd) {
    throw new Error('A valid Horizon opt-out date range is required.');
  }

  return { windowEnd: windowEnd.toISOString(), windowStart: windowStart.toISOString() };
};

const getLatestEvent = (events: HorizonEventLog[]): HorizonEventLog | undefined =>
  events.reduce<HorizonEventLog | undefined>((latest, event) => {
    if (!latest || Date.parse(event.eventDateUTC) > Date.parse(latest.eventDateUTC)) return event;
    return latest;
  }, undefined);

const worthFailureDescriptions = [
  'request failed for url',
  'failed to search for a business by external id',
];

const worthDecisionDescriptions = ['not qualified', 'do not contact', 'donotcontact'];

const isWorthUnavailableEvent = (event: HorizonEventLog) => {
  const eventType = event.eventType.toLowerCase();
  const eventDescription = event.eventDescription.toLowerCase();

  return (
    eventType === eventTypes.worthEvaluationFailure ||
    (eventType === eventTypes.worthEvaluated &&
      (eventDescription.startsWith('failed to ') ||
        worthFailureDescriptions.some((description) => eventDescription.includes(description))))
  );
};

const isWorthDecisionEvent = (event: HorizonEventLog) =>
  event.eventType.toLowerCase() === eventTypes.worthEvaluated &&
  worthDecisionDescriptions.some((description) => event.eventDescription.toLowerCase().includes(description));

/** Selects the latest opt-out event, prioritizing Worth decisions over Worth availability and Global Payments. */
const selectOptOutEvent = (events: HorizonEventLog[]): HorizonEventLog | undefined => {
  const worthEvents = events.filter((event) => isWorthDecisionEvent(event));
  const worthUnavailableEvents = events.filter((event) => isWorthUnavailableEvent(event));
  const globalPaymentsEvents = events.filter(
    (event) =>
      event.eventType.toLowerCase() === eventTypes.existingLeadExpired ||
      (event.eventType.toLowerCase() === eventTypes.phaseRulesEvaluated &&
        event.eventDescription.toLowerCase().includes(eventOutcomes.failed)),
  );
  const candidates =
    worthEvents.length > 0
      ? worthEvents
      : worthUnavailableEvents.length > 0
        ? worthUnavailableEvents
        : globalPaymentsEvents;

  return getLatestEvent(candidates);
};

/** Creates the progressively loaded details for a selected opt-out event. */
export const createHorizonOptOutDetails = (events: HorizonEventLog[]): HorizonOptOutDetails | null => {
  const selectedEvent = selectOptOutEvent(events);
  if (!selectedEvent) return null;

  return {
    optOutReason: selectedEvent.eventDescription,
    optOutTimeUTC: selectedEvent.eventDateUTC,
    type: isWorthUnavailableEvent(selectedEvent)
      ? 'Worth unavailable'
      : selectedEvent.eventType.toLowerCase() === eventTypes.worthEvaluated
        ? 'Worth'
        : 'Global Payments',
  };
};
